import { useEffect, useMemo, useRef, useState } from "react";
import {
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
} from "@mui/material";
import * as api from "../../../shared/lib/api";

type OptionItem = {
	label: string;
	value: any;
};

interface ListApiSelectProps {
	parameter: any;
	allParameters: any[];
	values: any;
	setFieldValue: (field: string, value: any) => void;
	disabled?: boolean;
	required?: boolean;
	error?: boolean;
	touched?: boolean;
}

const normalizeParamName = (name: string) => name.replace(/^{|}$/g, "");

const MONTH_MAP: Record<string, string> = {
	"01": "Janeiro",
	"02": "Fevereiro",
	"03": "Março",
	"04": "Abril",
	"05": "Maio",
	"06": "Junho",
	"07": "Julho",
	"08": "Agosto",
	"09": "Setembro",
	"10": "Outubro",
	"11": "Novembro",
	"12": "Dezembro",
};

const getFormikFieldKey = (p: any) => `${p.name}|${p.api_id}`;

export default function ListApiSelect({
	parameter,
	allParameters,
	values,
	setFieldValue,
	disabled,
	required,
	error,
	touched,
}: ListApiSelectProps) {
	const [options, setOptions] = useState<OptionItem[]>([]);
	const [loading, setLoading] = useState(false);
	const requestSeq = useRef(0);
	const payloadRef = useRef<Record<string, any>>({});
	const isGroup =
		!!parameter.ui?.group_key && Array.isArray(parameter.ui?.group_fields);
	const label = parameter.ui?.group_label || parameter.description;

	const hasError = !!(touched && error);

	const parameterName = useMemo(() => {
		// backend espera sem {}
		// exemplo: "{mounter_code}" -> "mounter_code"
		return normalizeParamName(parameter.name);
	}, [parameter.name]);

	const dependsOn = useMemo(() => {
		const deps: string[] = parameter.ui?.depends_on ?? [];
		const result: Record<string, any> = {};
		const entries: Array<[string, any]> = [];

		deps.forEach((dep) => {
			const found = allParameters.find((p) => {
				return (
					p.api_id === parameter.api_id && normalizeParamName(p.name) === dep
				);
			});
			if (found) {
				const value = values[getFormikFieldKey(found)];
				result[dep] = value;
				entries.push([dep, value]);
			}
		});

		return {
			payload: result,
			key: JSON.stringify(entries),
		};
	}, [parameter.api_id, parameter.ui, allParameters, values]);

	useEffect(() => {
		payloadRef.current = dependsOn.payload;
	}, [dependsOn.payload]);

	const currentValue = useMemo(() => {
		if (isGroup) {
			const fields: string[] = parameter.ui.group_fields;
			const groupValues: Record<string, any> = {};

			fields.forEach((f) => {
				const found = allParameters.find((p) => {
					return (
						p.api_id === parameter.api_id && normalizeParamName(p.name) === f
					);
				});
				if (found) {
					groupValues[f] = values[getFormikFieldKey(found)];
				}
			});

			const isComplete = fields.every((f) => {
				const v = groupValues[f];
				return v !== undefined && v !== null && String(v).length > 0;
			});

			if (!isComplete) return "";

			const matched = options.find((o) => {
				if (!o || typeof o.value !== "object" || !o.value) return false;
				return fields.every(
					(f) => String(o.value?.[f] ?? "") === String(groupValues[f] ?? ""),
				);
			});

			return matched ? matched.label : "";
		}

		const fieldValue = values[getFormikFieldKey(parameter)];
		if (
			fieldValue === undefined ||
			fieldValue === null ||
			String(fieldValue).length === 0
		)
			return "";

		const matched = options.find((o) => {
			if (o && typeof o.value === "object" && o.value) {
				return String(o.value?.[parameterName] ?? "") === String(fieldValue);
			}
			return String(o?.value ?? "") === String(fieldValue);
		});

		if (matched) {
			return typeof matched.value === "object" ? matched.label : matched.value;
		}

		const hasObjectOptions = options.some(
			(o) => o && typeof o.value === "object",
		);
		return hasObjectOptions ? "" : fieldValue;
	}, [isGroup, parameter, allParameters, values, options, parameterName]);

	useEffect(() => {
		if (disabled) return;

		let mounted = true;
		const currentSeq = ++requestSeq.current;

		async function load() {
			try {
				setLoading(true);

				const payload = {
					final_api_id: parameter.api_id,
					parameter_name: parameterName,
					selected: payloadRef.current,
				};

				const resp: any = await api.post(
					"product/parameters/options/",
					payload,
				);
				let opts: OptionItem[] = resp?.data?.content?.options ?? [];

				if (isGroup) {
					if (
						parameterName === "year_reference" ||
						parameterName === "month_reference"
					) {
						opts = opts.map((o) => {
							const parts = o.label.split("/");
							if (parts.length === 2) {
								const [month, year] = parts;
								const monthName = MONTH_MAP[month];
								if (monthName) {
									return {
										...o,
										label: `${monthName}/${year}`,
									};
								}
							}
							return o;
						});
					}
				}

				if (mounted && currentSeq === requestSeq.current) setOptions(opts);
			} finally {
				if (mounted && currentSeq === requestSeq.current) setLoading(false);
			}
		}

		load();

		return () => {
			mounted = false;
		};
	}, [parameter.api_id, parameterName, dependsOn.key, disabled, isGroup]);

	useEffect(() => {
		if (
			isGroup &&
			(parameterName === "year_reference" ||
				parameterName === "month_reference") &&
			options.length > 0 &&
			!currentValue
		) {
			const firstOption = options[0];
			const fields: string[] = parameter.ui?.group_fields || [];
			const valueObj = firstOption.value || {};

			fields.forEach((f) => {
				const found = allParameters.find((p) => {
					return (
						p.api_id === parameter.api_id && normalizeParamName(p.name) === f
					);
				});
				if (found) {
					setFieldValue(getFormikFieldKey(found), valueObj[f] ?? "");
				}
			});
		}
	}, [
		isGroup,
		parameterName,
		options,
		currentValue,
		parameter,
		allParameters,
		setFieldValue,
	]);

	const handleChange = (selected: OptionItem) => {
		if (isGroup) {
			const fields: string[] = parameter.ui.group_fields;
			const valueObj = selected.value || {};

			fields.forEach((f) => {
				const found = allParameters.find((p) => {
					return (
						p.api_id === parameter.api_id && normalizeParamName(p.name) === f
					);
				});
				if (!found) return;
				setFieldValue(getFormikFieldKey(found), valueObj[f] ?? "");
			});

			return;
		}

		// select simples:
		// - se vier value escalar, usa direto
		// - se vier objeto, tenta pegar pelo nome do parâmetro
		const raw = selected.value;
		const finalValue =
			raw && typeof raw === "object" ? (raw[parameterName] ?? "") : raw;

		setFieldValue(getFormikFieldKey(parameter), finalValue);
	};

	return (
		<FormControl
			fullWidth
			disabled={loading || disabled}
			error={hasError}
			required={required}
		>
			<InputLabel>{label}</InputLabel>

			<Select
				label={label}
				value={currentValue || ""}
				onChange={(e) => {
					const selected = options.find((o) => {
						if (o.value === e.target.value) return true;
						return false;
					});

					// Fallback: match por label quando o value é objeto
					const selectedFallback =
						selected || options.find((o) => o.label === String(e.target.value));

					if (selectedFallback) handleChange(selectedFallback);
				}}
			>
				{options.map((o, idx) => (
					<MenuItem
						key={`${o.label}-${idx}`}
						// Para group select com value objeto, não dá pra usar objeto como value do Select.
						// Então usamos label como value do MenuItem e tratamos no fallback acima.
						value={typeof o.value === "object" ? o.label : o.value}
					>
						{o.label}
					</MenuItem>
				))}
			</Select>
			{hasError && <FormHelperText>Campo obrigatório</FormHelperText>}
		</FormControl>
	);
}
